import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { RecognitionBadge } from "@/components/recognition-badge/RecognitionBadge.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import { useRecognitionHistoryFilters } from "@/pages/settings/recognitions/index/recognition-history.filters.tsx";
import { RecognitionHistoryActionsMenu } from "@/pages/settings/recognitions/widgets/RecognitionHistoryActionsMenu.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useRecognitionHistory } from "@/queries/use-recognitions.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiEmployeeRecognition,
  ApiRecognitionHistoryFilter,
} from "@/types/recognitions/recognition.types.ts";
import {
  DotSeparator,
  EmptyStateBase,
  FontAwesomeIcon,
  Page,
  PaginationBlock,
  Stack,
  Table,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

export function RecognitionHistoryTab() {
  const { t } = useTranslation();
  const { renderEmployeeName, renderTimeAgo } = useFormatter();
  const [, setSearchParams] = useSearchParams();

  const {
    filter: { filter, typedFilters },
  } = useAppFilter<ApiRecognitionHistoryFilter>({
    filters: useRecognitionHistoryFilters(),
    source: "Recognition history",
  });

  const [searchTerm, setSearchTerm] = useState<string | undefined>(typedFilters.search);

  useDebounceUpdate({
    data: searchTerm,
    timeout: 300,
    execute: (value) => {
      setSearchParams((params) => {
        if (value) params.set("search", value);
        else params.delete("search");
        params.set("page", "1");
        return params;
      });
    },
  });

  const { data, isPending } = useRecognitionHistory(filter);

  const columns = useMemo<ColumnDef<ApiEmployeeRecognition>[]>(() => {
    const columnHelper = createColumnHelper<ApiEmployeeRecognition>();
    const inner: DisplayColumnDef<ApiEmployeeRecognition, unknown>[] = [
      columnHelper.display({
        id: "employee",
        header: t("core:employee"),
        ...ColumnSizes.employee,
        cell: (cell) => {
          const employee = cell.row.original.employee;
          return (
            <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
              <EmployeeAvatar employee={employee} size={"md"} />
              <Text className="font-semibold text-default">{renderEmployeeName(employee)}</Text>
            </Stack>
          );
        },
      }),
      columnHelper.display({
        id: "recognition",
        header: t("recognition:column_recognition"),
        ...ColumnSizes.wide,
        cell: (cell) => {
          const item = cell.row.original;
          return (
            <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
              <RecognitionBadge template={item.recognition} size={"3xs"} />
              <Stack gap={"xs"}>
                <Text>{item.recognition_title}</Text>
                {item.revoked_by && item.datetime_revoked_at && (
                  <DotSeparator className={"text-warning"} dotClassName={"text-warning"}>
                    <Text>
                      {t("recognition:revoked_by", {
                        name: renderEmployeeName(item.revoked_by),
                      })}
                    </Text>
                    <Text>{renderTimeAgo(item.datetime_revoked_at)}</Text>
                  </DotSeparator>
                )}
              </Stack>
            </Stack>
          );
        },
      }),
      columnHelper.display({
        id: "awarded_by",
        header: t("recognition:column_given_by"),
        ...ColumnSizes.middle,
        cell: (cell) => {
          const awardedBy = cell.row.original.awarded_by;
          return (
            <EmployeeHoverCard employee={awardedBy} position={getEmployeePositionTitle(awardedBy)}>
              <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
                <EmployeeAvatar employee={awardedBy} size={"sm"} />
                <Text>{renderEmployeeName(awardedBy)}</Text>
              </Stack>
            </EmployeeHoverCard>
          );
        },
      }),
      columnHelper.display({
        id: "comment",
        header: t("common:comment"),
        ...ColumnSizes.narrow,
        cell: (cell) =>
          cell.row.original.comment ? (
            <Stack justify={"center"} items={"center"}>
              <Tooltip>
                <TooltipTrigger>
                  <FontAwesomeIcon icon={"fa-light fa-comment"} />
                </TooltipTrigger>
                <TooltipContent>{cell.row.original.comment}</TooltipContent>
              </Tooltip>
            </Stack>
          ) : (
            ""
          ),
      }),
      columnHelper.display({
        id: "date",
        header: t("common:date"),
        ...ColumnSizes.date,
        cell: (cell) => <Text>{renderTimeAgo(cell.row.original.datetime_awarded_at)}</Text>,
      }),
    ];
    return inner;
  }, [t, renderEmployeeName, renderTimeAgo]);

  return (
    <Page.StickyLayout
      toolbar={
        <Search
          type={"text"}
          value={searchTerm}
          placeholder={t("recognition:history_search_placeholder")}
          clearable={true}
          onInput={(e) => setSearchTerm(e.currentTarget.value)}
          onClear={() => setSearchTerm(undefined)}
        />
      }
      footer={
        data ? (
          <PaginationBlock
            metadata={data.metadata}
            onPageChange={(newPage) =>
              setSearchParams((params) => {
                params.set("page", newPage.toString());
                return params;
              })
            }
          />
        ) : undefined
      }
    >
      {!isPending && data?.items.length === 0 ? (
        <EmptyStateBase
          title={t("recognition:history_empty_title")}
          description={t("recognition:history_empty_description")}
        />
      ) : (
        <Table
          data={data?.items ?? []}
          columns={columns}
          showSkeleton={isPending}
          stickyHeader={true}
          renderRowAction={(row) => <RecognitionHistoryActionsMenu recognition={row.original} />}
        />
      )}
    </Page.StickyLayout>
  );
}
